import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useMenemosineQuery } from './hooks/useMenemosineQuery'
import { MENEMOSINE_ENTITY_TYPES } from './menemosineConstants'

const INITIAL_FILTERS = {
  namespace: '',
  tenantId: '',
  objectId: '',
  entity: '',
}

function MenemosinePage() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [hasSearched, setHasSearched] = useState(false)
  const { audits, isLoading, isError, search } = useMenemosineQuery()

  const isFilterIncomplete = Object.values(filters).some((value) => !value)

  function handleFieldChange(field) {
    return (event) => {
      const { value } = event.target
      setFilters((current) => ({ ...current, [field]: value }))
    }
  }

  function handleFilter() {
    if (isFilterIncomplete) return
    setHasSearched(true)
    search(filters)
  }

  function handleViewDetails(auditId) {
    navigate(`/workspace/menemosine/details/${auditId}`)
  }

  const showEmptyState = hasSearched && !isLoading && !isError && audits.length === 0

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Auditorias
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              id="namespace"
              label="Digite um namespace"
              fullWidth
              value={filters.namespace}
              onChange={handleFieldChange('namespace')}
            />
            <TextField
              id="tenant"
              label="Digite um tenant"
              fullWidth
              value={filters.tenantId}
              onChange={handleFieldChange('tenantId')}
            />
            <TextField
              id="search"
              label="Digite o ID"
              fullWidth
              value={filters.objectId}
              onChange={handleFieldChange('objectId')}
            />
            <TextField
              id="type"
              select
              label="Entidade"
              value={filters.entity}
              onChange={handleFieldChange('entity')}
              sx={{ minWidth: 220 }}
              fullWidth
            >
              {MENEMOSINE_ENTITY_TYPES.map((entityType) => (
                <MenuItem key={entityType.value} value={entityType.value}>
                  {entityType.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box sx={{ textAlign: 'right' }}>
            <Tooltip title="A busca trará resultados referentes a : Contrato, Contatos e Prazo">
              <span>
                <Button variant="contained" onClick={handleFilter} disabled={isFilterIncomplete}>
                  Filtrar
                </Button>
              </span>
            </Tooltip>
          </Box>

          {isFilterIncomplete && (
            <Typography variant="caption" color="text.secondary">
              Preencha namespace, tenant, ID e entidade para buscar.
            </Typography>
          )}
        </Stack>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Erro ao buscar a auditoria. Tente novamente.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data de criação</TableCell>
              <TableCell>Data Últ. atualização</TableCell>
              <TableCell>Quantidade de versões</TableCell>
              <TableCell>Entidade</TableCell>
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} sx={{ p: 0 }}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}

            {showEmptyState && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum registro de ação encontrado
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              audits.map((audit) => (
                <TableRow key={audit._id}>
                  <TableCell>{audit.createdAt}</TableCell>
                  <TableCell>{audit.changedAt}</TableCell>
                  <TableCell>{audit.versionsCount}</TableCell>
                  <TableCell>{audit.entity}</TableCell>
                  <TableCell>
                    <Tooltip title="Visualizar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewDetails(audit._id)}
                        aria-label="Visualizar"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default MenemosinePage
